import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import ConsultationExamOrdersPanel from './ConsultationExamOrdersPanel';
import ConsultationCareReferralsPanel from './ConsultationCareReferralsPanel';
import ConsultationRestDaysPanel from './ConsultationRestDaysPanel';
import NurseVitalsSection from '../nurse/workspace/NurseVitalsSection';
import NurseCareTestPanel from '../nurse/workspace/NurseCareTestPanel';
import consultationService from '../../services/consultationService';
import useConsultationActModuleCounts from './hooks/useConsultationActModuleCounts';
import Icon from '../common/Icons';
import './ConsultationActModules.css';
import '../nurse/workspace/NurseConsultationWorkspace.css';

export const CONSULTATION_ACT_MODULE_IDS = {
  EXAMS: 'exams',
  REFERRALS: 'referrals',
  REST: 'rest',
  NURSING: 'nursing'
};

const MODULE_DEFS = [
  {
    id: CONSULTATION_ACT_MODULE_IDS.EXAMS,
    emoji: '🔬',
    title: 'Examens complémentaires',
    hdKey: 'EXAM_PANEL_TITLE',
    hint: 'Analyses · radiographies · échographies · bons PDF',
    hintHdKey: 'EXAM_PANEL_HINT',
    tone: 'teal',
    panelTone: 'exams'
  },
  {
    id: CONSULTATION_ACT_MODULE_IDS.REFERRALS,
    emoji: '🏥',
    title: 'Orientations & prises en charge',
    hdKey: 'REFERRAL_PANEL_TITLE',
    hint: 'Hôpital · ophtalmo · pédiatrie · lunettes · bons PDF partenaires',
    hintHdKey: 'REFERRAL_PANEL_HINT',
    tone: 'amber',
    panelTone: 'referrals'
  },
  {
    id: CONSULTATION_ACT_MODULE_IDS.REST,
    emoji: '🛌',
    title: 'Jours de repos',
    hdKey: 'REST_DAYS_PANEL_TITLE',
    hint: 'Certificat médical d’arrêt · durée · impression PDF',
    hintHdKey: 'REST_DAYS_PANEL_HINT',
    tone: 'green',
    panelTone: 'rest'
  },
  {
    id: CONSULTATION_ACT_MODULE_IDS.NURSING,
    emoji: '🩺',
    title: 'Soins, tests & constantes',
    hdKey: 'NURSING_PANEL_TITLE',
    hint: 'Actes infirmiers · tests · vaccins · constantes',
    hintHdKey: 'NURSING_PANEL_HINT',
    tone: 'cyan',
    panelTone: 'nursing'
  }
];

const SUCCESS_MESSAGES = {
  [CONSULTATION_ACT_MODULE_IDS.EXAMS]: "Demandes d'examens enregistrées.",
  [CONSULTATION_ACT_MODULE_IDS.REFERRALS]: 'Orientations enregistrées.',
  [CONSULTATION_ACT_MODULE_IDS.REST]: 'Certificat de jours de repos enregistré.'
};

function NursingActsModulePanel({
  consultation,
  workSiteId,
  toast,
  readOnly,
  onConsultationUpdated,
  onSuccess
}) {
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const loadTimeline = useCallback(async () => {
    if (!consultation?.id || !workSiteId) {
      setTimeline(null);
      return;
    }

    setTimelineLoading(true);
    try {
      const res = await consultationService.getNursingTimeline(consultation.id, {
        site_id: workSiteId
      });
      if (res.success) {
        setTimeline(res.data || null);
      } else {
        setTimeline(null);
      }
    } catch {
      setTimeline(null);
    } finally {
      setTimelineLoading(false);
    }
  }, [consultation?.id, workSiteId]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline]);

  return (
    <div className="consult-nursing-module nr-consult-workspace">
      {!readOnly ? (
        <NurseVitalsSection
          consultation={consultation}
          viewerSiteId={workSiteId}
          onUpdated={() => {
            if (typeof onConsultationUpdated === 'function') onConsultationUpdated();
            if (typeof onSuccess === 'function') onSuccess();
          }}
          onTimelineRefresh={loadTimeline}
        />
      ) : null}

      <NurseCareTestPanel
        consultationId={consultation.id}
        toast={toast}
        timeline={timeline}
        timelineLoading={timelineLoading}
        onTimelineRefresh={loadTimeline}
      />
    </div>
  );
}

function ModulePanel({
  module,
  consultation,
  onClose,
  onSuccess,
  labels,
  readOnly = false,
  workSiteId,
  toast,
  onConsultationUpdated
}) {
  const panelId = `${module.id}-panel`;

  const renderPanel = () => {
    switch (module.id) {
      case CONSULTATION_ACT_MODULE_IDS.EXAMS:
        return <ConsultationExamOrdersPanel consultation={consultation} readOnly={readOnly} onSuccess={onSuccess} />;
      case CONSULTATION_ACT_MODULE_IDS.REFERRALS:
        return (
          <ConsultationCareReferralsPanel
            consultation={consultation}
            embedded
            readOnly={readOnly}
            onSuccess={onSuccess}
          />
        );
      case CONSULTATION_ACT_MODULE_IDS.REST:
        return (
          <ConsultationRestDaysPanel consultation={consultation} embedded readOnly={readOnly} onSuccess={onSuccess} />
        );
      case CONSULTATION_ACT_MODULE_IDS.NURSING:
        return (
          <NursingActsModulePanel
            consultation={consultation}
            workSiteId={workSiteId}
            toast={toast}
            readOnly={readOnly}
            onConsultationUpdated={onConsultationUpdated}
            onSuccess={onSuccess}
          />
        );
      default:
        return null;
    }
  };

  return (
    <section
      id={panelId}
      className={`consult-act-module-panel consult-act-module-panel--${module.panelTone}`}
      aria-labelledby={`${panelId}-title`}
    >
      <header className="consult-act-module-panel__head">
        <div className="consult-act-module-panel__head-main">
          <span className="consult-act-module-panel__icon" aria-hidden>
            {module.emoji}
          </span>
          <div>
            <h2 id={`${panelId}-title`} className="consult-act-module-panel__title">
              {labels.title}
            </h2>
            <p className="consult-act-module-panel__hint">{labels.hint}</p>
          </div>
        </div>
        <button
          type="button"
          className="consult-act-module-panel__close"
          onClick={onClose}
          aria-label={`Fermer ${labels.title}`}
        >
          <Icon name="times" size={14} />
          <span>Fermer</span>
        </button>
      </header>
      <div className="consult-act-module-panel__body">{renderPanel()}</div>
    </section>
  );
}

/**
 * Barre d’activation des modules complémentaires (examens, orientations, repos).
 * Les panneaux sont masqués par défaut pour alléger la page de consultation.
 */
export default function ConsultationActModules({
  consultation,
  consultationReady,
  isHeadDoctor = false,
  enableNursingActs = false,
  workSiteId = '',
  onConsultationUpdated,
  headDoctorCopy = null,
  readOnly = false,
  onModuleSuccess,
  toast
}) {
  const barId = useId();
  const [activeModules, setActiveModules] = useState(() => new Set());
  const [countsRefreshKey, setCountsRefreshKey] = useState(0);
  const { counts } = useConsultationActModuleCounts(
    consultation?.id,
    countsRefreshKey,
    workSiteId
  );

  const modules = useMemo(
    () =>
      MODULE_DEFS.map((mod) => ({
        ...mod,
        title: isHeadDoctor && headDoctorCopy?.[mod.hdKey] ? headDoctorCopy[mod.hdKey] : mod.title,
        hint:
          isHeadDoctor && headDoctorCopy?.[mod.hintHdKey] ? headDoctorCopy[mod.hintHdKey] : mod.hint,
        lockedHint:
          isHeadDoctor && headDoctorCopy?.[mod.hintHdKey]
            ? headDoctorCopy[mod.hintHdKey]
            : 'Disponible après enregistrement de la consultation.'
      })).filter((mod) => (mod.id === CONSULTATION_ACT_MODULE_IDS.NURSING ? enableNursingActs : true)),
    [isHeadDoctor, headDoctorCopy, enableNursingActs]
  );

  const toggleModule = useCallback(
    (moduleId) => {
      if (!consultationReady) return;
      setActiveModules((prev) => {
        const next = new Set(prev);
        if (next.has(moduleId)) {
          next.delete(moduleId);
        } else {
          next.add(moduleId);
        }
        return next;
      });
    },
    [consultationReady]
  );

  const closeModule = useCallback((moduleId) => {
    setActiveModules((prev) => {
      if (!prev.has(moduleId)) return prev;
      const next = new Set(prev);
      next.delete(moduleId);
      return next;
    });
  }, []);

  const handleSuccess = useCallback(
    (moduleId) => {
      setCountsRefreshKey((k) => k + 1);
      if (typeof toast === 'function') {
        toast('success', SUCCESS_MESSAGES[moduleId] || 'Enregistré.');
      }
      if (typeof onModuleSuccess === 'function') {
        onModuleSuccess(moduleId);
      }
    },
    [onModuleSuccess, toast]
  );

  const activeCount = activeModules.size;
  const totalContent = counts.exams + counts.referrals + counts.rest + counts.nursing;

  return (
    <div className="consult-act-modules" aria-labelledby={`${barId}-label`}>
      <div className="consult-act-modules__intro">
        <div>
          <p id={`${barId}-label`} className="consult-act-modules__label">
            Actes complémentaires
          </p>
          <p className="consult-act-modules__desc">
            {consultationReady
              ? readOnly
                ? 'Consultation terminée — ouvrez un module pour consulter ou imprimer les actes existants.'
                : 'Activez uniquement les modules nécessaires pour cette consultation.'
              : 'Enregistrez le dossier clinique pour débloquer examens, orientations et jours de repos.'}
          </p>
        </div>
            {consultationReady && readOnly ? (
              <span className="consult-act-modules__summary consult-act-modules__summary--readonly">
                Consultation clôturée · consultation des actes
              </span>
            ) : null}
            {consultationReady && !readOnly && totalContent > 0 ? (
          <span className="consult-act-modules__summary" aria-live="polite">
            {totalContent} élément{totalContent > 1 ? 's' : ''} au dossier
          </span>
        ) : null}
      </div>

      <div className="consult-act-modules__toolbar" role="toolbar" aria-label="Modules complémentaires">
        {modules.map((mod) => {
          const isActive = activeModules.has(mod.id);
          const count = counts[mod.id] ?? 0;
          const btnId = `${barId}-${mod.id}`;

          return (
            <button
              key={mod.id}
              type="button"
              id={btnId}
              className={[
                'consult-act-module-btn',
                `consult-act-module-btn--${mod.tone}`,
                isActive ? 'consult-act-module-btn--active' : '',
                !consultationReady ? 'consult-act-module-btn--locked' : '',
                count > 0 ? 'consult-act-module-btn--filled' : ''
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => toggleModule(mod.id)}
              disabled={!consultationReady}
              aria-expanded={isActive}
              aria-controls={isActive ? `${mod.id}-panel` : undefined}
              title={!consultationReady ? mod.lockedHint : mod.hint}
            >
              <span className="consult-act-module-btn__icon" aria-hidden>
                {mod.emoji}
              </span>
              <span className="consult-act-module-btn__text">
                <span className="consult-act-module-btn__title">{mod.title}</span>
                <span className="consult-act-module-btn__meta">
                  {isActive ? 'Module ouvert' : count > 0 ? `${count} au dossier` : 'Cliquer pour ouvrir'}
                </span>
              </span>
              {count > 0 ? (
                <span className="consult-act-module-btn__badge" aria-label={`${count} élément(s)`}>
                  {count}
                </span>
              ) : null}
              <Icon
                name={isActive ? 'chevronDown' : 'plus'}
                size={14}
                className="consult-act-module-btn__chevron"
                aria-hidden
              />
            </button>
          );
        })}
      </div>

      {activeCount > 0 ? (
        <div className="consult-act-modules__stack">
          {modules
            .filter((mod) => activeModules.has(mod.id))
            .map((mod) => (
              <ModulePanel
                key={mod.id}
                module={mod}
                consultation={consultation}
                labels={{ title: mod.title, hint: mod.hint }}
                onClose={() => closeModule(mod.id)}
                onSuccess={() => handleSuccess(mod.id)}
                readOnly={readOnly}
                workSiteId={workSiteId}
                toast={toast}
                onConsultationUpdated={onConsultationUpdated}
              />
            ))}
        </div>
      ) : null}
    </div>
  );
}
